import {
  Button,
  Checkbox,
  Flex,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconInfoCircle, IconPlus } from "@tabler/icons-react";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  type IChart,
  type ILogbookCharts,
  setChartsData,
  setChartsIncludeUnverified,
} from "src/redux/slices/entriesGeneratorSlice";

import { isChartNumeric } from "../Charts/Chart.utils";
import { useCodeDescriptions } from "../Charts/useCodeDescriptions";
import ChartSettingsForm from "./ChartSettingsForm";
import { finalizeChart, removeChart } from "./CreateChartButton.utils";

const groupChartsByColumn = (charts: IChart[]) => {
  const groups: Array<{
    sectionIndex: number;
    name: string;
    charts: IChart[];
  }> = [];
  charts.forEach(chart => {
    let group = groups.find(
      g => g.sectionIndex === chart.sectionIndex && g.name === chart.name,
    );
    if (!group) {
      group = {
        sectionIndex: chart.sectionIndex,
        name: chart.name,
        charts: [],
      };
      groups.push(group);
    }
    group.charts.push(chart);
  });
  return groups;
};

interface Props {
  opened: boolean;
  close: () => void;
  data: ILogbookCharts[];
  setData: Dispatch<SetStateAction<ILogbookCharts[]>>;
  includeUnverified: boolean;
  onIncludeUnverifiedChange: (checked: boolean) => void;
}

const CreateChartModal = ({
  opened,
  close,
  data,
  setData,
  includeUnverified,
  onIncludeUnverifiedChange,
}: Props) => {
  const dispatch = useDispatch();
  const logbooks = useSelector(state => state.entriesGeneratorSlice.logbooks);
  const ilIds = useMemo(
    () =>
      [
        ...new Set(
          data.flatMap(log =>
            log.data.flatMap(chart => (chart.il ? [chart.il] : [])),
          ),
        ),
      ].sort(),
    [data],
  );
  const descriptionsByList = useCodeDescriptions(opened ? ilIds : []);

  const updateChart = (id: string, updater: (chart: IChart) => IChart) => {
    setData(prev =>
      prev.map(log => ({
        ...log,
        data: log.data.map(chart => (chart.id === id ? updater(chart) : chart)),
      })),
    );
  };

  const handleAddChart = (lbid: string, sectionIndex: number, name: string) => {
    setData(prev =>
      prev.map(log => {
        if (log.lbid !== lbid) return log;
        const columnCharts = log.data
          .map((chart, i) => ({ chart, i }))
          .filter(
            ({ chart }) =>
              chart.sectionIndex === sectionIndex && chart.name === name,
          );
        const template = columnCharts[0]?.chart;
        if (!template) return log;

        if (template.valueOnly)
          return {
            ...log,
            data: log.data.map(chart =>
              chart.id === template.id
                ? {
                    ...chart,
                    valueOnly: false,
                    type: isChartNumeric(chart) ? "line" : "pie",
                  }
                : chart,
            ),
          };

        const newChart: IChart = {
          ...template,
          id: crypto.randomUUID(),
          type: "pie",
          valueSource: { kind: "count" },
          trendline: false,
          sortBy: undefined,
          sortDirection: undefined,
          groupSmall: undefined,
          title: undefined,
          filter: undefined,
          filterVals: undefined,
          sumVals: undefined,
        };
        const insertAt = columnCharts[0].i;
        const nextData = [...log.data];
        nextData.splice(insertAt, 0, newChart);
        return { ...log, data: nextData };
      }),
    );
  };

  const handleRemoveChart = (id: string) => {
    setData(prev =>
      prev.map(log => ({ ...log, data: removeChart(log.data, id) })),
    );
  };

  const handleCreate = () => {
    dispatch(setChartsIncludeUnverified(includeUnverified));
    dispatch(
      setChartsData(
        data.map(log => ({
          ...log,
          data: log.data.map(chart => finalizeChart(chart, log.data)),
        })),
      ),
    );
  };

  const renderChart = (chart: IChart, charts: IChart[]) => (
    <ChartSettingsForm
      key={chart.id}
      chart={chart}
      charts={charts}
      descriptionsByList={descriptionsByList}
      onChange={updater => updateChart(chart.id, updater)}
      onRemove={() => handleRemoveChart(chart.id)}
    />
  );

  const renderModalTitle = useCallback(
    () => (
      <Flex gap={8}>
        <Text size="lg" fw="bold">
          Charts
        </Text>
        <Tooltip
          w={200}
          multiline
          transitionProps={{ duration: 200 }}
          label="Below you can set the charts for those fields which contained some values">
          <IconInfoCircle style={{ cursor: "pointer" }} />
        </Tooltip>
      </Flex>
    ),
    [],
  );

  return (
    <Modal
      size="100%"
      styles={{ content: { maxWidth: 900 } }}
      title={renderModalTitle()}
      opened={opened}
      onClose={close}>
      <Checkbox
        mb={16}
        checked={includeUnverified}
        onChange={event =>
          onIncludeUnverifiedChange(event.currentTarget.checked)
        }
        label="Include unverified entries (Open, Re-Verify)"
      />
      {data.length === 0 && (
        <Text>Provided fields do not contain any values</Text>
      )}
      {data.map((el: ILogbookCharts) => (
        <Stack key={el.lbid} gap={12} mb={24}>
          <Text fw={600} size="lg">
            {logbooks.find(log => log.value === el.lbid)?.label}
          </Text>
          {groupChartsByColumn(el.data).map(group => (
            <Paper
              key={`${group.sectionIndex}-${group.name}`}
              withBorder
              radius="md"
              p="md">
              <Group justify="space-between" wrap="nowrap">
                <Text fw={600}>{group.name}</Text>
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconPlus size={14} />}
                  onClick={() =>
                    handleAddChart(el.lbid, group.sectionIndex, group.name)
                  }>
                  Add chart
                </Button>
              </Group>
              {group.charts.every(chart => chart.valueOnly) && (
                <Text size="sm" c="dimmed" mt={8}>
                  No chart for this column. It is still available as a value or
                  filter for other charts in this section.
                </Text>
              )}
              {group.charts
                .filter(chart => !chart.valueOnly)
                .map(chart => renderChart(chart, el.data))}
            </Paper>
          ))}
        </Stack>
      ))}
      <Group
        w="100%"
        bg="var(--mantine-color-body)"
        style={{
          position: "sticky",
          bottom: 0,
          paddingBottom: 16,
          marginBottom: -16,
          zIndex: 1,
        }}
        justify="right"
        mt={10}>
        <Button onClick={handleCreate} disabled={data.length === 0}>
          Create
        </Button>
      </Group>
    </Modal>
  );
};

export default CreateChartModal;
